import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Settings, Check, Trash2, CalendarDays, UtensilsCrossed, ListChecks, ShoppingCart, Sparkles, Volume2, Play, Smartphone, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SOUNDS, playNotificationSound, getStoredSound, setStoredSound } from "@/lib/notificationSounds";
import { isPushSupported, currentPermission, subscribeToPush, unsubscribeFromPush, isSubscribedLocally } from "@/lib/pushNotifications";

const TYPE_ICONS = {
  calendar_event_created: CalendarDays,
  calendar_event_assigned: CalendarDays,
  meal_plan_updated: UtensilsCrossed,
  todo_assigned: ListChecks,
  todo_completed: ListChecks,
  shopping_list_updated: ShoppingCart,
  auto_import: Sparkles,
};

const TYPE_TINT = {
  calendar_event_created: "text-sky-600 bg-sky-100",
  calendar_event_assigned: "text-emerald-600 bg-emerald-100",
  meal_plan_updated: "text-amber-600 bg-amber-100",
  todo_assigned: "text-violet-600 bg-violet-100",
  todo_completed: "text-teal-600 bg-teal-100",
  shopping_list_updated: "text-rose-600 bg-rose-100",
  auto_import: "text-stone-600 bg-stone-100",
};

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff < 60) return "nå";
  if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t siden`;
  return `${Math.floor(diff / 86400)} d siden`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState([]);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [sound, setSound] = useState(() => getStoredSound());
  const [pushSupported] = useState(() => isPushSupported());
  const [pushPerm, setPushPerm] = useState(() => currentPermission());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceBusy, setDeviceBusy] = useState(null);
  const prevUnread = useRef(null);
  const firstLoad = useRef(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.listNotifications(30);
      setItems(res.data?.items || []);
      const newUnread = res.data?.unread_count || 0;
      // Chime when unread count grows — skip very first load so we don't buzz on mount.
      if (!firstLoad.current && prevUnread.current !== null && newUnread > prevUnread.current) {
        playNotificationSound(getStoredSound());
      }
      prevUnread.current = newUnread;
      firstLoad.current = false;
      setUnread(newUnread);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const handleClickItem = async (n) => {
    try {
      if (!n.read) {
        await api.markNotificationRead(n.id);
      }
    } catch { /* ignore */ }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const handleMarkAll = async () => {
    try {
      await api.markAllNotificationsRead();
      toast.success("Alle varsler markert som lest");
      load();
    } catch { toast.error("Kunne ikke markere alle"); }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await api.deleteNotification(id);
      load();
    } catch { /* ignore */ }
  };

  const handleClearRead = async () => {
    try {
      await api.clearReadNotifications();
      toast.success("Leste varsler fjernet");
      load();
    } catch { toast.error("Kunne ikke fjerne leste"); }
  };

  const openPrefs = async () => {
    setPrefsOpen(true);
    setPrefsLoading(true);
    try {
      const res = await api.getNotificationPreferences();
      setPrefs(res.data?.preferences || []);
    } catch { setPrefs([]); }
    finally { setPrefsLoading(false); }
    // Refresh push state
    try {
      setPushPerm(currentPermission());
      const sub = await isSubscribedLocally();
      setPushSubscribed(sub);
      // Load registered devices
      const d = await api.listPushDevices();
      setDevices(d.data?.devices || []);
    } catch { /* ignore */ }
  };

  const reloadDevices = async () => {
    try {
      const d = await api.listPushDevices();
      setDevices(d.data?.devices || []);
    } catch { /* ignore */ }
  };

  const handleTestDevice = async (endpoint) => {
    setDeviceBusy(endpoint);
    try {
      await api.testPushDevice(endpoint);
      toast.success("Test sendt — sjekk telefonen din");
      reloadDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende test");
      if (err.response?.status === 410) reloadDevices();
    } finally { setDeviceBusy(null); }
  };

  const handleDeleteDevice = async (endpoint, name) => {
    if (!window.confirm(`Fjerne ${name} fra pålogga enheter? Du får ikke push på denne enheten før du aktiverer igjen.`)) return;
    setDeviceBusy(endpoint);
    try {
      await api.deletePushDevice(endpoint);
      toast.success("Enhet fjernet");
      reloadDevices();
    } catch { toast.error("Kunne ikke fjerne"); }
    finally { setDeviceBusy(null); }
  };

  const handleTogglePush = async (wantOn) => {
    if (!pushSupported) {
      toast.error("Nettleseren støtter ikke push-varsler");
      return;
    }
    setPushBusy(true);
    try {
      if (wantOn) {
        await subscribeToPush();
        setPushSubscribed(true);
        setPushPerm("granted");
        toast.success("Push aktivert — du får varsler på låseskjermen");
      } else {
        await unsubscribeFromPush();
        setPushSubscribed(false);
        toast.success("Push slått av for denne enheten");
      }
      reloadDevices();
    } catch (err) {
      toast.error(err.message || "Kunne ikke endre push-status");
    } finally { setPushBusy(false); }
  };

  const handleTestPush = async () => {
    setPushBusy(true);
    try {
      const res = await api.sendTestPush();
      if (res.data?.sent > 0) {
        toast.success(`Test sendt til ${res.data.sent} enhet${res.data.sent > 1 ? "er" : ""}`);
      } else {
        toast.error("Ingen enheter å sende til — aktiver push først");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test feilet");
    } finally { setPushBusy(false); }
  };

  const togglePref = async (key, enabled) => {
    const next = prefs.map((p) => (p.key === key ? { ...p, enabled } : p));
    setPrefs(next);
    try {
      const asObject = Object.fromEntries(next.map((p) => [p.key, p.enabled]));
      await api.updateNotificationPreferences(asObject);
    } catch { toast.error("Kunne ikke lagre innstilling"); }
  };

  const handleSoundChange = (key) => {
    setSound(key);
    setStoredSound(key);
    // Preview so the user immediately hears what they picked
    playNotificationSound(key);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="relative p-1.5 md:p-2 hover:bg-amber-50 rounded-md text-stone-500 hover:text-amber-700 transition-colors"
            title="Varsler"
            data-testid="inapp-notification-bell"
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-amber-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center"
                data-testid="inapp-notification-badge"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[22rem] p-0" align="end" data-testid="inapp-notification-popover">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-stone-100 bg-amber-50/60">
            <h3 className="text-sm font-semibold text-stone-800">Varsler</h3>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="text-[11px] text-amber-700 hover:text-amber-900 font-medium px-2 py-1 rounded hover:bg-amber-100"
                  data-testid="notif-mark-all-read-btn"
                  title="Marker alle som lest"
                >
                  <Check className="h-3.5 w-3.5 inline mr-0.5" />Les alle
                </button>
              )}
              <button
                onClick={openPrefs}
                className="p-1 rounded hover:bg-amber-100 text-stone-500 hover:text-amber-700"
                title="Varsel-innstillinger"
                data-testid="notif-settings-btn"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-10 text-center" data-testid="notif-empty-state">
              <Bell className="h-8 w-8 mx-auto text-stone-300 mb-2" />
              <p className="text-sm text-stone-500">Ingen varsler ennå</p>
              <p className="text-xs text-stone-400 mt-1">Her dukker det opp nye avtaler, endringer i ukemeny og oppgaver.</p>
            </div>
          ) : (
            <div className="max-h-[26rem] overflow-y-auto divide-y divide-stone-100">
              {items.map((n) => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                const tint = TYPE_TINT[n.type] || "text-stone-600 bg-stone-100";
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClickItem(n)}
                    className={`w-full text-left px-3 py-2.5 flex gap-2.5 items-start transition-colors group ${n.read ? "bg-white hover:bg-stone-50" : "bg-amber-50/50 hover:bg-amber-50"}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${tint}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-1.5">
                        <p className={`text-sm leading-snug flex-1 ${n.read ? "text-stone-700" : "text-stone-900 font-semibold"}`}>
                          {n.title}
                        </p>
                        {!n.read && <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-600 flex-shrink-0" />}
                      </div>
                      {n.body && <p className="text-xs text-stone-600 mt-0.5 line-clamp-2">{n.body}</p>}
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[11px] text-stone-400">{relTime(n.created_at)}</span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => handleDelete(e, n.id)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleDelete(e, n.id); }}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-rose-600 transition-all cursor-pointer"
                          title="Slett varsel"
                          data-testid={`notif-delete-${n.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {items.length > 0 && (
            <div className="px-3 py-2 border-t border-stone-100 flex items-center justify-between bg-stone-50/60">
              <span className="text-[11px] text-stone-400">Leses automatisk i 30 dager</span>
              <button
                onClick={handleClearRead}
                className="text-[11px] text-stone-500 hover:text-rose-600 font-medium"
                data-testid="notif-clear-read-btn"
              >
                Fjern leste
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Preferences dialog */}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="notif-prefs-dialog">
          <DialogHeader>
            <DialogTitle>Varsel-innstillinger</DialogTitle>
            <DialogDescription>
              Velg hvilke hendelser du vil få varsel om. Endringer lagres automatisk.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Push (low-level phone/lock-screen) */}
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3" data-testid="push-section">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-sky-700" />
                  <span className="text-sm font-semibold text-stone-800">Push på telefonen</span>
                </div>
                {pushSupported && pushPerm !== "denied" && (
                  <Switch
                    checked={pushSubscribed}
                    disabled={pushBusy}
                    onCheckedChange={(v) => handleTogglePush(!!v)}
                    data-testid="push-toggle"
                  />
                )}
              </div>
              {!pushSupported && (
                <p className="text-xs text-stone-600">Nettleseren eller enheten din støtter ikke push-varsler. På iPhone må du først installere appen på hjem-skjermen.</p>
              )}
              {pushSupported && pushPerm === "denied" && (
                <p className="text-xs text-rose-700">Du har blokkert varsler i nettleseren. Åpne innstillinger for PantryBuddy og tillat varsler for å aktivere push.</p>
              )}
              {pushSupported && pushPerm !== "denied" && (
                <>
                  <p className="text-xs text-stone-600">
                    {pushSubscribed
                      ? "Varsler kommer direkte på låseskjermen — selv når appen er lukket."
                      : "Aktiver for å få varsler på låseskjermen selv når appen er lukket."}
                  </p>
                  {pushSubscribed && (
                    <button
                      onClick={handleTestPush}
                      disabled={pushBusy}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium text-sky-800 hover:bg-sky-50 disabled:opacity-50"
                      data-testid="push-test-btn"
                    >
                      {pushBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Smartphone className="h-3.5 w-3.5" />}
                      Send test til alle enheter
                    </button>
                  )}
                </>
              )}

              {/* Registered devices list */}
              {devices.length > 0 && (
                <div className="mt-3 pt-3 border-t border-sky-200/70">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-sky-800 mb-2">
                    Dine enheter ({devices.length})
                  </p>
                  <ul className="space-y-1.5">
                    {devices.map((d) => (
                      <li
                        key={d.endpoint}
                        className="flex items-center justify-between gap-2 rounded-md bg-white border border-sky-100 px-2.5 py-2"
                        data-testid={`push-device-${d.device_id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <Smartphone className="h-3.5 w-3.5 text-sky-700 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-stone-800 truncate">{d.name}</p>
                            <p className="text-[10px] text-stone-400">
                              Sist brukt {relTime(d.last_used_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            onClick={() => handleTestDevice(d.endpoint)}
                            disabled={deviceBusy === d.endpoint}
                            className="p-1 rounded hover:bg-sky-100 text-sky-700 disabled:opacity-40"
                            title="Send test til denne enheten"
                            data-testid={`push-device-test-${d.device_id}`}
                          >
                            {deviceBusy === d.endpoint ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            onClick={() => handleDeleteDevice(d.endpoint, d.name)}
                            disabled={deviceBusy === d.endpoint}
                            className="p-1 rounded hover:bg-rose-50 text-stone-400 hover:text-rose-600 disabled:opacity-40"
                            title="Fjern enhet"
                            data-testid={`push-device-delete-${d.device_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sound picker */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Volume2 className="h-4 w-4 text-amber-700" />
                <span className="text-sm font-semibold text-stone-800">Lyd ved nytt varsel</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {SOUNDS.map((s) => {
                  const active = sound === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => handleSoundChange(s.key)}
                      className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm transition-all ${active ? "bg-amber-600 text-white border-amber-700 shadow-sm" : "bg-white text-stone-700 border-stone-200 hover:border-amber-400"}`}
                      data-testid={`notif-sound-${s.key}`}
                    >
                      <span className="font-medium">{s.label}</span>
                      {s.key !== "off" && <Play className={`h-3.5 w-3.5 ${active ? "text-white" : "text-stone-400"}`} />}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-stone-500 mt-2">Klikk igjen for å forhåndshøre valget ditt.</p>
            </div>

            {prefsLoading && <p className="text-sm text-stone-500">Henter innstillinger…</p>}
            {!prefsLoading && prefs.length === 0 && <p className="text-sm text-stone-500">Ingen innstillinger tilgjengelig.</p>}
            {prefs.map((p) => (
              <div key={p.key} className="flex items-start justify-between gap-3 py-2 border-b border-stone-100 last:border-0">
                <label htmlFor={`pref-${p.key}`} className="text-sm text-stone-700 flex-1 cursor-pointer">
                  {p.label}
                </label>
                <Switch
                  id={`pref-${p.key}`}
                  checked={!!p.enabled}
                  onCheckedChange={(v) => togglePref(p.key, v)}
                  data-testid={`notif-pref-toggle-${p.key}`}
                />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
